import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Link2, Loader2, Play, Search, Tags } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const TABS = [
  { key: "related", label: "Related articles", icon: Link2 },
  { key: "terms", label: "Search terms", icon: Search },
];

/** Bulk review of things the KB can work out for itself: which articles should
 *  point at each other, and which words readers type that ought to be
 *  keywords. Both sides are proposals — nothing is written until Apply. */
export default function KBSuggestions() {
  const [tab, setTab] = useState("related");
  const [rel, setRel] = useState(null);
  const [terms, setTerms] = useState(null);
  const [ticks, setTicks] = useState({});          // slug -> Set of ids/terms
  const [busy, setBusy] = useState("");

  const loadRelated = useCallback(async () => {
    try {
      const { data } = await api.get("/kb/related/backfill");
      setRel(data);
      // Suggestions arrive pre-ticked — untick what you don't want.
      setTicks((t) => {
        const next = { ...t };
        (data.proposals || []).forEach((p) => {
          if (!next[p.slug]) next[p.slug] = new Set(p.suggestions.map((s) => s.article_id));
        });
        return next;
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load proposals"); }
  }, []);

  const loadTerms = useCallback(async () => {
    setBusy("terms");
    try {
      const { data } = await api.get("/kb/keywords/from-searches");
      setTerms(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't load search terms"); }
    finally { setBusy(""); }
  }, []);

  useEffect(() => { loadRelated(); }, [loadRelated]);
  useEffect(() => { if (tab === "terms" && !terms) loadTerms(); }, [tab, terms, loadTerms]);
  useEffect(() => {
    if (!rel?.running) return undefined;
    const t = setInterval(loadRelated, 4000);
    return () => clearInterval(t);
  }, [rel?.running, loadRelated]);

  const toggle = (key, value) => setTicks((t) => {
    const set = new Set(t[key] || []);
    if (set.has(value)) set.delete(value); else set.add(value);
    return { ...t, [key]: set };
  });

  const runBatch = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/kb/related/backfill/run", null, { params: { limit: 50 } });
      toast.success(`Working through ${data.batch} articles — this takes a few minutes`);
      await loadRelated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the batch"); }
    finally { setBusy(""); }
  };

  const applyRelated = async () => {
    const items = (rel?.proposals || []).map((p) => ({
      slug: p.slug, ids: [...(ticks[p.slug] || [])],
    })).filter((i) => i.ids.length);
    if (!items.length) return toast.error("Nothing is ticked");
    setBusy("apply");
    try {
      const { data } = await api.post("/kb/related/backfill/apply", { items });
      toast.success(`${data.applied} article${data.applied === 1 ? "" : "s"} updated`);
      await loadRelated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't apply those"); }
    finally { setBusy(""); }
  };

  const applyTerms = async () => {
    const items = (terms?.articles || []).map((a) => ({
      slug: a.slug, terms: [...(ticks[`t:${a.slug}`] || [])],
    })).filter((i) => i.terms.length);
    if (!items.length) return toast.error("Nothing is ticked");
    setBusy("apply");
    try {
      const { data } = await api.post("/kb/keywords/from-searches/apply", { items });
      toast.success(`${data.keywords_added} keyword${data.keywords_added === 1 ? "" : "s"} added to ${data.articles} article${data.articles === 1 ? "" : "s"}`);
      setTerms(null);
      await loadTerms();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't apply those"); }
    finally { setBusy(""); }
  };

  const relTicked = (rel?.proposals || []).reduce((n, p) => n + (ticks[p.slug]?.size || 0), 0);
  const termTicked = (terms?.articles || []).reduce((n, a) => n + (ticks[`t:${a.slug}`]?.size || 0), 0);

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="kb-suggestions-page">
      <Link to="/kb" className="mb-4 inline-flex items-center gap-1.5 text-xs text-slate-500 no-underline hover:text-primary dark:text-slate-400">
        <ArrowLeft className="h-3.5 w-3.5" /> Knowledge Base
      </Link>
      <h1 className="font-heading text-2xl font-semibold text-slate-900 dark:text-slate-50">Suggestions</h1>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Proposals the KB worked out on its own. Untick anything you disagree with, then apply.
      </p>

      <div className="mt-5 flex gap-1 border-b border-slate-200 dark:border-slate-800">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`kb-suggestions-tab-${t.key}`}
            className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
              tab === t.key ? "border-primary text-primary"
                : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400"}`}>
            <t.icon className="h-3.5 w-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "related" && (
        <div className="mt-4 space-y-3">
          <Card className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {rel
                ? `${(rel.remaining || 0).toLocaleString()} articles have nothing related yet · ${(rel.proposals || []).length} waiting for review · ${(rel.applied_total || 0).toLocaleString()} done`
                : "Loading…"}
              {rel?.running && ` · working: ${rel.progress || 0}/${rel.total || 0}`}
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={runBatch} disabled={!!busy || rel?.running}
                data-testid="kb-suggestions-run">
                {busy === "run" || rel?.running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Play className="mr-1.5 h-3.5 w-3.5" />}
                Suggest for 50 more
              </Button>
              <Button size="sm" onClick={applyRelated} disabled={!!busy || !relTicked}
                data-testid="kb-suggestions-apply-related">
                <Check className="mr-1.5 h-3.5 w-3.5" /> Apply {relTicked || ""}
              </Button>
            </div>
          </Card>

          {!(rel?.proposals || []).length && (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="kb-suggestions-related-empty">
              Nothing waiting. Press “Suggest for 50 more” to work through the backlog.
            </p>
          )}
          {(rel?.proposals || []).map((p) => (
            <Card key={p.slug} className="px-4 py-3" data-testid={`kb-suggestions-proposal-${p.slug}`}>
              <div className="flex items-baseline justify-between gap-3">
                <Link to={`/kb/a/${p.slug}`} className="truncate text-sm font-semibold text-slate-800 no-underline hover:text-primary dark:text-slate-100">
                  {p.name}
                </Link>
                <span className="shrink-0 text-[11px] text-slate-400">{(p.views || 0).toLocaleString()} views</span>
              </div>
              <div className="mt-2 space-y-1">
                {p.suggestions.map((s) => (
                  <label key={s.article_id} data-testid={`kb-suggestions-pick-${p.slug}-${s.article_id}`}
                    className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 hover:bg-slate-50 dark:hover:bg-slate-800/60">
                    <input type="checkbox" className="mt-0.5" checked={!!ticks[p.slug]?.has(s.article_id)}
                      onChange={() => toggle(p.slug, s.article_id)} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-xs text-slate-700 dark:text-slate-200">
                        {s.name}
                        {s.is_draft && <span className="ml-1.5 rounded bg-amber-100 px-1 text-[10px] font-semibold uppercase text-amber-700">Draft</span>}
                      </span>
                      {!!s.why && <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">{s.why}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === "terms" && (
        <div className="mt-4 space-y-3">
          <Card className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {terms
                ? `${terms.terms_scanned} search terms used ${terms.min_searches}+ times · ${(terms.articles || []).length} articles could adopt one · ${terms.clicks_logged} clicks recorded so far`
                : "Reading the search log…"}
            </p>
            <Button size="sm" onClick={applyTerms} disabled={!!busy || !termTicked}
              data-testid="kb-suggestions-apply-terms">
              <Tags className="mr-1.5 h-3.5 w-3.5" /> Add {termTicked || ""} as keywords
            </Button>
          </Card>
          {busy === "terms" && <p className="py-8 text-center text-sm text-slate-400">Working through the search log…</p>}
          {terms && !(terms.articles || []).length && (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="kb-suggestions-terms-empty">
              No search term has been used {terms.min_searches}+ times yet — check back once the KB has been searched a bit more.
            </p>
          )}
          {(terms?.articles || []).map((a) => (
            <Card key={a.slug} className="px-4 py-3" data-testid={`kb-suggestions-terms-${a.slug}`}>
              <Link to={`/kb/a/${a.slug}`} className="truncate text-sm font-semibold text-slate-800 no-underline hover:text-primary dark:text-slate-100">
                {a.name}
              </Link>
              <div className="mt-2 flex flex-wrap gap-2">
                {a.terms.map((t) => (
                  <label key={t.term} data-testid={`kb-suggestions-term-${a.slug}-${t.term.replace(/\s+/g, "-")}`}
                    className="flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 px-2 py-1 text-[11px] text-slate-700 hover:border-primary dark:border-slate-700 dark:text-slate-200">
                    <input type="checkbox" checked={!!ticks[`t:${a.slug}`]?.has(t.term)}
                      onChange={() => toggle(`t:${a.slug}`, t.term)} />
                    {t.term}
                    <span className="text-slate-400">· {t.searches} searches · {t.source}</span>
                  </label>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
